import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";

type DownloadedFile = {
	path: string;
	downloadUrl: string;
};

type Props = {
	open: boolean;
	onClose: () => void;
	downloaderName: string;
	files: DownloadedFile[];
};

export const DownloadResultDialog: React.FC<Props> = ({
	open,
	onClose,
	downloaderName,
	files,
}) => {
	const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

	const handleDownload = (file: DownloadedFile) => {
		const link = document.createElement("a");
		link.href = file.downloadUrl;
		link.download = file.path.split("/").pop() || "file";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleCopyUrl = async (file: DownloadedFile, index: number) => {
		await navigator.clipboard.writeText(file.downloadUrl);
		setCopiedIndex(index);
		setTimeout(() => setCopiedIndex(null), 2000);
	};

	const getFileName = (path: string) => path.split("/").pop() || path;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<CheckCircleIcon color="success" />
					<Typography variant="h6">Stažení a nahrání dokončeno</Typography>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Typography color="text.secondary" sx={{ mb: 2 }}>
					Soubory pro <strong>{downloaderName}</strong> byly staženy a nahrány
					do systému jako nová verze:
				</Typography>
				<List dense>
					{files.map((file, index) => (
						<ListItem
							key={file.path}
							secondaryAction={
								<Box>
									<Tooltip
										title={copiedIndex === index ? "Zkopírováno!" : "Kopírovat URL"}
									>
										<IconButton
											size="small"
											onClick={() => handleCopyUrl(file, index)}
										>
											<ContentCopyIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Stáhnout">
										<IconButton
											size="small"
											onClick={() => handleDownload(file)}
											color="primary"
										>
											<DownloadIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Box>
							}
							sx={{
								bgcolor: "action.hover",
								borderRadius: 1,
								mb: 0.5,
							}}
						>
							<ListItemIcon sx={{ minWidth: 32 }}>
								<Typography variant="body2" color="text.secondary">
									{index + 1}.
								</Typography>
							</ListItemIcon>
							<ListItemText
								primary={getFileName(file.path)}
								secondary={file.path}
								primaryTypographyProps={{ fontWeight: 500 }}
								secondaryTypographyProps={{
									variant: "caption",
									sx: { wordBreak: "break-all" },
								}}
							/>
						</ListItem>
					))}
				</List>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={onClose}>
					Zavřít
				</Button>
			</DialogActions>
		</Dialog>
	);
};
